import { useEffect, useRef, useState } from "react";
import { Check, Wrench, BookOpen, Zap, Brain } from "lucide-react";

const TONE = {
  t: "var(--color-primary)",
  k: "#5b9df0",
  s: "#ef9f27",
  a: "#8f86ea",
};

const LABS = [
  {
    name: "Clinic Portal Breakpoint",
    ids: [
      ["T4500", "t"],
      ["K4502", "k"],
      ["S4503", "s"],
    ],
    delay: "0s",
  },
  {
    name: "Phantom Ticket",
    ids: [
      ["A0001", "a"],
      ["T4509", "t"],
    ],
    delay: "-5s",
  },
  {
    name: "SMB Relay Lab",
    ids: [
      ["K4508", "k"],
      ["S4510", "s"],
      ["A4503", "a"],
    ],
    delay: "-10s",
  },
];

/* one falling chip per lane, staggered so the lanes never fire in unison */
const FALLING = [
  { id: "T4500", tone: "t", lane: 12.5, delay: "0s" },
  { id: "T4509", tone: "t", lane: 12.5, delay: "1.6s" },
  { id: "K4502", tone: "k", lane: 37.5, delay: "0.3s" },
  { id: "K4508", tone: "k", lane: 37.5, delay: "1.9s" },
  { id: "S4503", tone: "s", lane: 62.5, delay: "0.6s" },
  { id: "S4510", tone: "s", lane: 62.5, delay: "2.2s" },
  { id: "A0001", tone: "a", lane: 87.5, delay: "0.9s" },
  { id: "A4503", tone: "a", lane: 87.5, delay: "2.5s" },
];

const HOPPERS = [
  {
    key: "t",
    icon: Wrench,
    label: "Tasks",
    value: 17,
    total: 606,
    pct: 3,
    gained: 10,
    delay: "2.7s",
  },
  {
    key: "k",
    icon: BookOpen,
    label: "Knowledge",
    value: 34,
    total: 392,
    pct: 9,
    gained: 8,
    delay: "3s",
  },
  {
    key: "s",
    icon: Zap,
    label: "Skills",
    value: 19,
    total: 238,
    pct: 8,
    gained: 7,
    delay: "3.3s",
  },
  {
    key: "a",
    icon: Brain,
    label: "Abilities",
    value: 8,
    total: 135,
    pct: 6,
    gained: 9,
    delay: "3.6s",
  },
];

const LANES = [12.5, 37.5, 62.5, 87.5];

const RING_R = 25;
const RING_C = 2 * Math.PI * RING_R;
const OVERALL = 5.7;

function useInView(threshold = 0.2) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ob = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setSeen(true);
          ob.disconnect();
        }
      },
      { threshold },
    );
    ob.observe(el);
    return () => ob.disconnect();
  }, [threshold]);
  return [ref, seen];
}

/* counts 0 → end once `run` flips true */
function useCount(end, run, duration = 1400, delay = 0) {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!run) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setV(end);
      return;
    }
    let raf;
    const t0 = performance.now() + delay;
    const step = (t) => {
      const p = Math.min(1, Math.max(0, (t - t0) / duration));
      setV(Math.round(end * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [run, end, duration, delay]);
  return v;
}

function Chip({ id, tone, className = "", style }) {
  return (
    <span
      className={`qv-mono rounded-md border px-1.5 py-0.5 text-[7.5px] ${className}`}
      style={{
        color: TONE[tone],
        borderColor: `color-mix(in srgb, ${TONE[tone]} 40%, transparent)`,
        background: `color-mix(in srgb, ${TONE[tone]} 9%, transparent)`,
        ...style,
      }}
    >
      {id}
    </span>
  );
}

function Hopper({ hop, seen }) {
  const Icon = hop.icon;
  const colour = TONE[hop.key];
  const value = useCount(hop.value, seen, 1400, 400);
  return (
    <div
      className={`relative overflow-hidden rounded-xl border border-(--color-border) bg-(--color-bg-card) p-4 ${
        seen ? "lp-tksa-hit" : ""
      }`}
      style={{ color: colour, "--lp-tksa-delay": hop.delay }}
    >
      {/* wash where the chips land */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -top-10 left-1/2 h-24 w-24 -translate-x-1/2 rounded-full opacity-[0.1] blur-3xl"
        style={{ background: colour }}
      />
      {/* +n floater */}
      {seen && (
        <span
          className="lp-tksa-plus absolute right-3 top-3 qv-mono text-[9px]"
          style={{ "--lp-tksa-delay": hop.delay }}
        >
          +{hop.gained}
        </span>
      )}

      <div className="mb-2 flex items-center gap-1.5 qv-mono text-[8.5px] uppercase tracking-wider">
        <Icon className="h-3 w-3" />
        {hop.label}
      </div>

      <div className="qv-serif text-[25px] font-medium leading-none">
        {value}{" "}
        <span className="text-[11px] text-(--color-text-muted)">
          / {hop.total}
        </span>
      </div>

      <span className="mt-3 block h-1 overflow-hidden rounded-full bg-(--color-bg)">
        <span
          className="lp-tksa-shimmer relative block h-full rounded-full"
          style={{
            background: colour,
            width: seen ? `${(hop.value / hop.total) * 100}%` : 0,
            transition: "width 1.4s cubic-bezier(.2,.8,.3,1) .4s",
          }}
        />
      </span>
      <div className="mt-1 qv-mono text-[8.5px] text-(--color-text-muted)">
        {hop.pct}%
      </div>
    </div>
  );
}

export default function TksaSection() {
  const [ref, seen] = useInView();
  const overall = useCount(Math.round(OVERALL * 10), seen, 1600, 400) / 10;

  return (
    <section id="tksa" className="relative py-20 lg:py-28">
      <div className="mx-auto max-w-350 px-5 md:px-10">
        {/* heading */}
        <div className="lp-reveal lp-down mx-auto max-w-2xl text-center">
          <div className="mb-3 flex items-center justify-center gap-2 qv-mono text-[11px] font-medium uppercase tracking-[0.24em] text-(--color-primary)">
            <span className="h-px w-6 bg-(--color-primary) opacity-50" />
            TKSA
            <span className="h-px w-6 bg-(--color-primary) opacity-50" />
          </div>
          <h2 className="qv-glitch mx-auto max-w-2xl text-[34px] sm:text-[42px] font-medium leading-tight tracking-tight text-(--color-text-primary)">
            Labs go in.{" "}
            <span className="lp-highlight">Coverage comes out.</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-[14.5px] leading-relaxed text-(--color-text-secondary)">
            Every scenario declares the exact Tasks, Knowledge, Skills and
            Abilities it exercises — and drops them straight into your
            organisation's coverage.
          </p>
        </div>

        <div ref={ref} className="lp-reveal mt-14">
          {/* 1 · conveyor of solved labs */}
          <div
            className="relative overflow-hidden border-y border-(--color-border) py-6"
            style={{
              background:
                "linear-gradient(180deg, color-mix(in srgb, var(--color-primary) 3.5%, transparent), transparent)",
            }}
          >
            <span className="absolute left-4 top-2 z-3 qv-mono text-[8px] uppercase tracking-[0.16em] text-(--color-text-muted)">
              Labs solved · live
            </span>
            {/* dashed rail */}
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 top-1/2 h-px"
              style={{
                background:
                  "repeating-linear-gradient(90deg, var(--color-border-solid) 0 6px, transparent 6px 12px)",
              }}
            />
            {/* edge fades */}
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 left-0 z-3 w-24"
              style={{
                background:
                  "linear-gradient(90deg, var(--color-bg), transparent)",
              }}
            />
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 right-0 z-3 w-24"
              style={{
                background:
                  "linear-gradient(270deg, var(--color-bg), transparent)",
              }}
            />

            {LABS.map((lab) => (
              <div
                key={lab.name}
                className={`absolute top-1/2 flex -translate-y-1/2 items-center gap-2 whitespace-nowrap rounded-xl border border-(--color-border-solid) bg-(--color-bg-card) px-3 py-2 shadow-[0_8px_24px_-10px_rgba(0,0,0,0.85)] ${
                  seen ? "lp-tksa-belt" : ""
                }`}
                style={{
                  animationDelay: lab.delay,
                  left: seen ? undefined : "-18%",
                }}
              >
                <span className="grid h-3.5 w-3.5 shrink-0 place-items-center rounded border border-(--color-primary)/45 bg-(--color-primary-100) text-(--color-primary)">
                  <Check className="h-2 w-2" strokeWidth={3.4} />
                </span>
                <span className="text-[11.5px] font-semibold text-(--color-text-primary)">
                  {lab.name}
                </span>
                <span className="flex gap-1">
                  {lab.ids.map(([id, tone]) => (
                    <Chip key={id} id={id} tone={tone} />
                  ))}
                </span>
              </div>
            ))}
          </div>

          {/* 2 · chips fall down the funnels into their hoppers.
                 The lanes are set to the centres of a 4-column grid, so this is
                 desktop-only — on the 2-column mobile grid they wouldn't line up. */}
          <div className="relative hidden h-24 md:block">
            {LANES.map((lane) => (
              <span
                key={lane}
                aria-hidden="true"
                className="pointer-events-none absolute inset-y-0 w-px"
                style={{
                  left: `${lane}%`,
                  background:
                    "linear-gradient(180deg, transparent, var(--color-border-solid) 30%, var(--color-border-solid) 70%, transparent)",
                }}
              />
            ))}
            {seen &&
              FALLING.map((c) => (
                <Chip
                  key={`${c.id}-${c.delay}`}
                  id={c.id}
                  tone={c.tone}
                  className="lp-tksa-fall pointer-events-none absolute text-[8.5px]"
                  style={{
                    left: `${c.lane}%`,
                    marginLeft: "-1.4rem",
                    animationDelay: c.delay,
                  }}
                />
              ))}
          </div>

          {/* 3 · the four hoppers */}
          <div className="mt-8 grid grid-cols-2 gap-3 md:mt-0 md:grid-cols-4">
            {HOPPERS.map((hop) => (
              <Hopper key={hop.key} hop={hop} seen={seen} />
            ))}
          </div>

          {/* 4 · overall coverage */}
          <div className="relative mt-5 flex items-center justify-center gap-4 overflow-hidden rounded-xl border border-(--color-border) qv-card qv-noise p-4">
            <span
              aria-hidden="true"
              className="lp-tksa-scan pointer-events-none absolute left-0 right-0 h-12 bg-[linear-gradient(180deg,transparent,var(--color-primary-50),transparent)]"
            />
            <div className="relative h-14.5 w-14.5 shrink-0">
              <svg
                width="58"
                height="58"
                viewBox="0 0 58 58"
                className="-rotate-90"
              >
                <circle
                  cx="29"
                  cy="29"
                  r={RING_R}
                  fill="none"
                  stroke="var(--color-bg)"
                  strokeWidth="5"
                />
                <circle
                  cx="29"
                  cy="29"
                  r={RING_R}
                  fill="none"
                  stroke="var(--color-primary)"
                  strokeWidth="5"
                  strokeLinecap="round"
                  strokeDasharray={RING_C}
                  strokeDashoffset={
                    seen ? RING_C * (1 - OVERALL / 100) : RING_C
                  }
                  style={{
                    transition: "stroke-dashoffset 1.8s ease .4s",
                    filter: "drop-shadow(0 0 5px var(--color-shadow-primary))",
                  }}
                />
              </svg>
              <span className="absolute inset-0 grid place-items-center qv-mono text-[10.5px] text-(--color-primary)">
                {overall.toFixed(1)}%
              </span>
            </div>
            <div className="text-[12.5px] leading-relaxed text-(--color-text-secondary)">
              <span className="qv-mono text-(--color-primary)">78 / 1,371</span>{" "}
              framework items covered
              <br />
              SCyWF_v1 · NIST 800-181 · scored from completed scenarios only
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
